import { User } from 'oidc-client-ts';
import { fetchOrganizations, type Organization, UserNotActivatedError } from './orgService';
import { httpClient } from './shared/httpClient';
import { ApiError } from './shared/apiError';

/**
 * Service for handling SSO cookie detection and validation
 * Enables cross-app authentication by detecting auth cookies from other Biamp apps
 */

export interface OrganizationInfo {
  name: string;
  domain: string;
}

interface SSOCookieService {
  detectSSOCookie: () => string | null;
  validateSSOToken: (token: string) => Promise<boolean>;
  fetchUserInfoFromToken: (token: string) => Promise<any | null>;
  exchangeSSOCookieForSession: () => Promise<User | null>;
  getUserFromSSOCookie: () => Promise<any | null>;
  getOrganizationInfoForValidUser: () => Promise<OrganizationInfo | null>;
  setSSOCookie: (token: string, expiresIn?: number) => void;
  clearSSOCookie: () => void;
}

/**
 * Parse cookies from document.cookie string
 */
const parseCookies = (): Record<string, string> => {
  return document.cookie
    .split(';')
    .map(cookie => cookie.trim())
    .filter(cookie => cookie.length > 0)
    .reduce((acc, cookie) => {
      const [key, value] = cookie.split('=');
      acc[key] = decodeURIComponent(value);
      return acc;
    }, {} as Record<string, string>);
};

/**
 * Fetch user info from OIDC UserInfo endpoint
 * @param accessToken The access token to validate
 * @returns User info object if valid, null otherwise
 */
const fetchUserInfoFromOIDC = async (accessToken: string): Promise<any | null> => {
  try {
    const issuer = process.env.REACT_APP_ZITADEL_ISSUER;
    if (!issuer) {
      console.error('REACT_APP_ZITADEL_ISSUER not configured');
      return null;
    }

    const userinfoEndpoint = `${issuer}/oidc/v1/userinfo`;
    console.log('Fetching user info from:', userinfoEndpoint);

    const userInfo = await httpClient.get<any>(userinfoEndpoint, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    console.log('Successfully fetched user info from OIDC endpoint');
    return userInfo;
  } catch (e) {
    if (e instanceof ApiError) {
      console.error('UserInfo request failed:', e.status, e.statusText);
      return null;
    }
    console.error('Error fetching user info from OIDC:', e);
    return null;
  }
};

const cookieSSOService: SSOCookieService = {
  /**
   * Detect SSO cookie from other Biamp apps (Workplace)
   * Workplace sets: access_token, membership_id, organization_id, refresh_token, user_id
   * @returns The access_token if found, null otherwise
   */
  detectSSOCookie: (): string | null => {
    // Default to true if not explicitly set to 'false'
    const isEnabled = process.env.REACT_APP_ENABLE_SSO_COOKIES !== 'false';

    if (!isEnabled) {
      console.log('SSO cookie detection is disabled');
      return null;
    }

    const cookies = parseCookies();

    // Log all cookies for debugging
    console.log('All cookies found:', Object.keys(cookies));
    console.log('Cookie details:', cookies);

    // Check for Workplace's access_token cookie
    if (cookies['access_token']) {
      console.log('SSO cookie detected: access_token (from Workplace)');
      console.log('Token preview:', cookies['access_token'].substring(0, 20) + '...');
      return cookies['access_token'];
    }

    console.log('No SSO cookie found (need access_token)');
    return null;
  },

  /**
   * Validate SSO token by calling OIDC UserInfo endpoint
   * @param token The access_token to validate
   * @returns Promise<boolean> true if valid, false otherwise
   */
  validateSSOToken: async (token: string): Promise<boolean> => {
    if (!token || typeof token !== 'string') {
      console.log('Invalid token format');
      return false;
    }

    // Validate by attempting to fetch user info
    const userInfo = await fetchUserInfoFromOIDC(token);

    if (userInfo) {
      console.log('Token validation passed - user info retrieved');
      return true;
    }

    console.log('Token validation failed - could not retrieve user info');
    return false;
  },

  /**
   * Fetch user info using the access token
   * @param token The access_token
   * @returns Promise<any | null> User info object or null
   */
  fetchUserInfoFromToken: async (token: string): Promise<any | null> => {
    return await fetchUserInfoFromOIDC(token);
  },

  /**
   * Exchange SSO cookie for a session
   * This attempts to use the access_token to establish a user session
   * @returns User object if successful, null otherwise
   */
  exchangeSSOCookieForSession: async (): Promise<User | null> => {
    try {
      const accessToken = cookieSSOService.detectSSOCookie();

      if (!accessToken) {
        console.log('No SSO cookie to exchange');
        return null;
      }

      // IMPORTANT: Validate the SSO token FIRST before checking existing sessions
      // This prevents using a stale OIDC session when the SSO cookie token is expired/invalid
      console.log('Validating SSO token from cookie');
      const userInfo = await fetchUserInfoFromOIDC(accessToken);

      if (!userInfo) {
        console.log('SSO token validation failed - could not retrieve user info');
        // Clear the invalid SSO cookie to prevent retry loops
        cookieSSOService.clearSSOCookie();
        return null;
      }

      console.log('Access token validated, user info retrieved:', userInfo.sub);

      // Create a minimal User object compatible with oidc-client-ts
      const now = Math.floor(Date.now() / 1000);
      // Default to 1 hour expiration (we don't know actual expiration without decoding JWT)
      const expiresIn = 3600;
      const expiresAt = now + expiresIn;

      const userObject = {
        id_token: '', // We don't have an id_token, only access_token
        access_token: accessToken,
        token_type: 'Bearer',
        scope: 'openid profile email',
        profile: {
          ...userInfo,
          sub: userInfo.sub,
          name: userInfo.name,
          email: userInfo.email,
          preferred_username: userInfo.preferred_username || userInfo.email,
          email_verified: userInfo.email_verified,
        },
        expires_at: expiresAt,
        expires_in: expiresIn,
        expired: false,
        scopes: ['openid', 'profile', 'email'],
        toStorageString: function() { return JSON.stringify(this); }
      } as unknown as User;

      // Store the user object in sessionStorage so OIDC client can find it
      const oidcKey = `oidc.user:${process.env.REACT_APP_ZITADEL_ISSUER}:${process.env.REACT_APP_ZITADEL_CLIENT_ID}`;
      sessionStorage.setItem(oidcKey, JSON.stringify(userObject));

      console.log('Successfully created User object from access_token and OIDC UserInfo');
      return userObject;
    } catch (error) {
      console.error('Error exchanging SSO cookie for session:', error);
      return null;
    }
  },

  /**
   * Extract user information from SSO cookie by calling OIDC UserInfo endpoint
   * @returns User profile information if available
   */
  getUserFromSSOCookie: async (): Promise<any | null> => {
    try {
      const accessToken = cookieSSOService.detectSSOCookie();

      if (!accessToken) {
        return null;
      }

      // Fetch user info from OIDC UserInfo endpoint
      const userInfo = await fetchUserInfoFromOIDC(accessToken);

      if (!userInfo) {
        return null;
      }

      return {
        sub: userInfo.sub,
        name: userInfo.name,
        email: userInfo.email,
        preferred_username: userInfo.preferred_username || userInfo.email,
        email_verified: userInfo.email_verified,
      };
    } catch (error) {
      console.error('Error getting user from SSO cookie:', error);
      return null;
    }
  },

  /**
   * Get organization name and domain for a valid SSO user
   * Validates the user and fetches their organization from the GraphQL API
   * @returns Organization info (name and domain) if found, null otherwise
   */
  getOrganizationInfoForValidUser: async (): Promise<OrganizationInfo | null> => {
    try {
      // First, validate that we have a valid SSO token
      const accessToken = cookieSSOService.detectSSOCookie();

      if (!accessToken) {
        console.log('No SSO cookie found');
        return null;
      }

      // Validate token by fetching user info
      const isValid = await cookieSSOService.validateSSOToken(accessToken);
      if (!isValid) {
        console.log('SSO token validation failed');
        return null;
      }

      console.log('Valid SSO user detected, fetching organizations...');

      // Get the organization_id from cookies (set by Workplace)
      const cookies = parseCookies();
      const organizationId = cookies['organization_id'];

      if (!organizationId) {
        console.log('No organization_id cookie found');
        return null;
      }

      // Fetch organizations from GraphQL API (uses access_token internally)
      const organizations = await fetchOrganizations();

      // Find the organization matching the cookie ID
      const selectedOrg = organizations.find((org: Organization) => org.id === organizationId);

      if (selectedOrg) {
        console.log('Found organization:', selectedOrg.name);
        return { name: selectedOrg.name, domain: selectedOrg.domain };
      }

      console.log('Organization not found in user memberships');
      return null;
    } catch (error) {
      if (error instanceof UserNotActivatedError) {
        console.error('User is not activated in this region:', error.message);
        // Clear SSO cookies to prevent retry loops
        cookieSSOService.clearSSOCookie();
      } else {
        console.error('Error getting organization name for valid user:', error);
      }
      return null;
    }
  },

  /**
   * Set SSO cookie for cross-app sharing
   * Note: In production, Workplace manages its own cookies. This is a no-op.
   * @param _token The access token (unused)
   * @param _expiresIn Token expiration time in seconds (unused)
   */
  setSSOCookie: (_token: string, _expiresIn = 3600): void => {
    // Default to true if not explicitly set to 'false'
    const isEnabled = process.env.REACT_APP_ENABLE_SSO_COOKIES !== 'false';

    if (!isEnabled) {
      console.log('SSO cookie setting is disabled');
      return;
    }

    // In production, this is managed by Workplace
    // This function is kept for backward compatibility but does nothing
    console.log('SSO cookie management delegated to Workplace');
  },

  /**
   * Clear all SSO cookies (Workplace cookies)
   */
  clearSSOCookie: (): void => {
    const cookieDomain = process.env.REACT_APP_SSO_COOKIE_DOMAIN || 'localhost';

    // Clear all Workplace SSO cookies
    const cookiesToClear = [
      'access_token',          // Workplace access token
      'refresh_token',         // Workplace refresh token
      'organization_id',       // Organization ID
      'membership_id',         // Membership ID
      'user_id',               // User ID
    ];

    cookiesToClear.forEach(cookieName => {
      document.cookie = `${cookieName}=; path=/; domain=${cookieDomain}; max-age=0`;
    });

    console.log('All SSO cookies cleared');
  },
};

export default cookieSSOService;
